import { useMutation, useQuery } from '@apollo/client';
import { notHttpUrl, badWords, runValidation } from '@seek/validators-js';
import {
  Stack,
  Button,
  Box,
  Actions,
  Autosuggest,
  Notice,
  Text,
  Inline,
} from 'braid-design-system';
import { Fragment, useState } from 'react';

import FILTERED_DISTRICTS from 'src/queries/filteredDistricts';

import ADD_KEYWORD from '../../queries/addPreferenceKeyword';
import ADD_DISTRICT from '../../queries/addPreferencesDistrict';

type Preference = 'industry' | 'location' | 'keyword';
interface AutosuggestValue {
  text: string;
  value?: string;
}

interface Props {
  preference: Preference;
  refetchAll: () => void;
}

const getSuggestions = (preference: Preference, data: any) => {
  if (preference === 'location') {
    return data?.filteredDistricts?.districts?.map((i: any) => ({
      value: i.id,
      text: i.displayTitle,
    }));
  }
  return [];
};

const getPlaceholder = (preference: Preference) => {
  if (preference === 'location') {
    return 'Enter your preferred location';
  }
  return 'Enter a keyword';
};

const getAddPreferenceMutation = (preference: Preference) => {
  if (preference === 'location') {
    return ADD_DISTRICT;
  }
  return ADD_KEYWORD;
};

const getAddPreferenceInput = (
  preference: Preference,
  autosuggestValue: AutosuggestValue,
) => {
  const { valid } = runValidation({
    fields: {
      value: autosuggestValue.text,
    },
    rules: {
      value: { validators: [notHttpUrl, badWords] },
    },
  });
  if (preference === 'location') {
    return {
      input: {
        id: autosuggestValue.value,
      },
      valid,
    };
  }

  return {
    input: autosuggestValue.text,
    valid,
  };
};

const AddPreference = ({ preference, refetchAll }: Props) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const emptyAutoSuggest = {
    text: '',
  };
  const [autosuggestValue, setAutosuggestValue] =
    useState<AutosuggestValue>(emptyAutoSuggest);

  const [addPreference] = useMutation(getAddPreferenceMutation(preference));

  const { data, refetch } = useQuery(FILTERED_DISTRICTS, {
    variables: { searchString: autosuggestValue.text },
  });

  const suggestions = getSuggestions(preference, data);

  const resetAddPreference = () => {
    setAutosuggestValue(emptyAutoSuggest);
    setShowAddForm(false);
    setShowValidation(false);
  };

  return (
    <Fragment>
      {showAddForm ? (
        <Box padding="medium" background="neutralLight">
          <Stack space="medium">
            <Autosuggest
              automaticSelection
              id={`${preference}Add`}
              placeholder={getPlaceholder(preference)}
              label=""
              value={autosuggestValue}
              onChange={(value) => {
                setAutosuggestValue(value);
                setShowValidation(false);
              }}
              onBlur={() => {
                refetch();
              }}
              onClear={() => {
                setAutosuggestValue(emptyAutoSuggest);
              }}
              suggestions={suggestions ?? { message: 'No results found' }}
            />
            <Actions>
              <Button
                onClick={() => {
                  const { input, valid } = getAddPreferenceInput(
                    preference,
                    autosuggestValue,
                  );
                  if (
                    autosuggestValue.value &&
                    preference === 'location' &&
                    valid
                  ) {
                    addPreference({
                      variables: {
                        input,
                      },
                    });
                    refetchAll();
                    resetAddPreference();
                  } else if (
                    preference === 'keyword' &&
                    autosuggestValue.text.length > 0 &&
                    valid
                  ) {
                    addPreference({
                      variables: {
                        input,
                      },
                    });
                    refetchAll();
                    resetAddPreference();
                  } else {
                    setShowValidation(true);
                  }
                }}
              >
                Add
              </Button>
              <Button
                variant="transparent"
                onClick={() => {
                  resetAddPreference();
                }}
              >
                Done
              </Button>
            </Actions>
            {showValidation && (
              <Notice tone="critical">
                <Text>
                  {preference === 'location'
                    ? `Please choose a valid ${preference} from the list`
                    : 'Please type a valid keyword'}
                </Text>
              </Notice>
            )}
          </Stack>
        </Box>
      ) : (
        <Inline space="none">
          <Button
            variant="ghost"
            tone="formAccent"
            onClick={() => setShowAddForm(true)}
          >
            Add another {preference}
          </Button>
        </Inline>
      )}
    </Fragment>
  );
};

export default AddPreference;
